import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Container
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  AdminPanelSettings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  background: theme.palette.mode === 'light' 
    ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)' 
    : 'linear-gradient(135deg, #1c1f2b 0%, #121418 100%)',
  justifyContent: 'center',
  alignItems: 'center',
}));

const LoginPanel = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 550,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(6),
  boxShadow: theme.shadows[6],
  borderRadius: theme.shape.borderRadius * 2,
  margin: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3),
    margin: theme.spacing(2),
    maxWidth: '95%',
  },
}));

const BrandLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}40`,
    },
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5, 0),
  marginTop: theme.spacing(1),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: theme.shadows[3],
  '&:hover': {
    boxShadow: theme.shadows[5],
  },
}));

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formValues, setFormValues] = useState({
    username: '',
    password: '',
    rememberMe: true
  });

  // Check if already logged in
  useEffect(() => {
    if (pb.authStore.isValid) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Format current date
  const currentDateUTC = new Date("2025-04-02 03:02:11");
  const formattedDate = currentDateUTC.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormValues({
      ...formValues,
      [name]: name === 'rememberMe' ? checked : value
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const authData = await pb.collection('admin').authWithPassword(
        formValues.username,
        formValues.password
      );
      
      if (authData) {
        // If not using "remember me", set session to expire after 24 hours
        if (!formValues.rememberMe) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          pb.authStore.save(pb.authStore.token, tomorrow);
        }
        
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginPanel elevation={6}>
        <BrandLogo>
          <Box 
            sx={{ 
              bgcolor: blue[600], 
              p: 1.2, 
              borderRadius: 2, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3
            }}
          >
            <AdminPanelSettings sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
               Admin
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formattedDate}
            </Typography>
          </Box>
        </BrandLogo>
        
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Welcome back
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Please sign in to access your admin dashboard
        </Typography>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 1 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <StyledTextField
            fullWidth
            required
            label="Username"
            name="username"
            value={formValues.username}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <StyledTextField
            fullWidth
            required
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formValues.password}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            alignItems: { xs: 'flex-start', sm: 'center' }
          }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={formValues.rememberMe}
                  onChange={handleChange}
                  name="rememberMe"
                  color="primary"
                />
              }
              label="Remember me"
            />
            <Link href="/forgot-password" variant="body2" underline="hover">
              Forgot password?
            </Link>
          </Box>
          
          <LoginButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </LoginButton>
        </Box>
        
        {/* <Divider sx={{ my: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Need Help?
          </Typography>
        </Divider>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: { xs: 2, sm: 3 },
          flexWrap: 'wrap'
        }}>
          <Link href="/contact-support" variant="body2" underline="hover">
            Contact Support
          </Link>
          <Link href="/help-center" variant="body2" underline="hover">
            Help Center
          </Link>
          <Link href="/security" variant="body2" underline="hover">
            Security
          </Link>
        </Box> */}
      </LoginPanel>
    </LoginContainer>
  );
};

export default Login;